import moment from "moment";
import "moment/locale/es";
moment.locale('es');

// Paleta del formato de papel del cliente (azules, oro y durazno), usada como acentos sobre
// paneles claros: sin rejillas ni bordes negros, para que no se vea como hoja de calculo.
const COLOR = {
  azul: '#288AC1',
  azulOscuro: '#1D5F86',
  azulTinte: '#EAF4FA',
  oro: '#E8B84B',
  durazno: '#E8935F',
  rojo: '#C0392B',
  verde: '#4E9A4E',
  verdeTinte: '#EDF6EC',
  amarilloTinte: '#FDF3D3',
  texto: '#1F2933',
  textoSuave: '#728090',
  panel: '#F5F8FB',
  divisor: '#E4EAF0',
  divisorFuerte: '#C9D6E2',
  casillaVacia: '#A9B4BF',
  blanco: '#FFFFFF',
};

const PALETA_GRUPOS = ['#F8D3C3', '#E5D3EE', '#CFE2F2', '#F6E6B4', '#D3EADB', '#F0CFDB'];

const MARGEN = 34;
const ANCHO_CONTENIDO = 612 - MARGEN * 2; // LETTER

// ---------------------------------------------------------------------------------------
// Lenguaje visual (el mismo en ordenDeMaquilaPdf.js). Cada tabla arma su propio arreglo de
// widths: pdfmake los muta.
// ---------------------------------------------------------------------------------------

const sinBordes = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

// Panel de datos: fondo claro, filete de color arriba, nada de rejilla.
const panel = (acento = COLOR.azul) => ({
  hLineWidth: (i) => (i === 0 ? 1.5 : 0),
  vLineWidth: () => 0,
  hLineColor: () => acento,
  fillColor: () => COLOR.panel,
  paddingLeft: () => 10,
  paddingRight: () => 10,
  paddingTop: (i) => (i === 0 ? 9 : 5),
  paddingBottom: (i, node) => (i === node.table.body.length - 1 ? 9 : 5),
});

// Tabla de datos: filete de color arriba, divisores tenues entre renglones, sin lineas
// verticales; el ultimo renglon es el de totales.
const tabla = (acento = COLOR.azul) => ({
  hLineWidth: (i, node) => {
    if (i === 0) return 1.5;
    if (i === 1 || i === node.table.body.length - 1) return 0.8;
    if (i === node.table.body.length) return 0;
    return 0.5;
  },
  vLineWidth: () => 0,
  hLineColor: (i, node) => {
    if (i === 0) return acento;
    if (i === 1 || i === node.table.body.length - 1) return COLOR.divisorFuerte;
    return COLOR.divisor;
  },
  paddingLeft: () => 5,
  paddingRight: () => 5,
  paddingTop: () => 4.5,
  paddingBottom: () => 4.5,
});

const lista = {
  hLineWidth: (i) => (i === 0 ? 0 : 0.5),
  vLineWidth: () => 0,
  hLineColor: () => COLOR.divisor,
  paddingLeft: () => 4,
  paddingRight: () => 10,
  paddingTop: () => 4,
  paddingBottom: () => 4,
};

const mostrar = (valor) => (valor === 0 ? '0' : valor === undefined || valor === null || valor === '' ? ' ' : String(valor));

const fecha = (val) => (val ? moment(val).format('DD/MM/YYYY') : '');

// Titulo de seccion como texto (no tabla): asi nunca se parte y `evitarTituloHuerfano`
// puede mandarlo a la siguiente pagina junto con su contenido.
const titulo = (texto, color = COLOR.azulOscuro) => ({
  text: texto.toUpperCase(),
  fontSize: 8.5,
  bold: true,
  color,
  margin: [0, 16, 0, 5],
  headlineLevel: 1,
});

const evitarTituloHuerfano = (nodo, siguientesEnPagina) => nodo.headlineLevel === 1 && siguientesEnPagina.length === 0;

const campo = (etiqueta, valor, extra = {}) => ({
  stack: [
    { text: etiqueta.toUpperCase(), style: 'etiqueta' },
    { text: mostrar(valor), style: 'valor', margin: [0, 2, 0, 0] },
  ],
  ...extra,
});

const campoNodo = (etiqueta, nodo, extra = {}) => ({
  stack: [{ text: etiqueta.toUpperCase(), style: 'etiqueta' }, { ...nodo, margin: [0, 3, 0, 0] }],
  ...extra,
});

// Casilla dibujada, con palomita si va marcada.
const casilla = (texto, activo) => ({
  width: 'auto',
  columns: [
    {
      width: 11,
      canvas: [
        {
          type: 'rect',
          x: 0,
          y: 1.5,
          w: 8.5,
          h: 8.5,
          lineWidth: 0.8,
          lineColor: activo ? COLOR.azul : COLOR.casillaVacia,
          color: activo ? COLOR.azul : COLOR.blanco,
        },
        ...(activo
          ? [
              {
                type: 'polyline',
                lineWidth: 1.3,
                lineColor: COLOR.blanco,
                points: [{ x: 2, y: 5.9 }, { x: 3.8, y: 7.8 }, { x: 6.8, y: 3.8 }],
              },
            ]
          : []),
      ],
    },
    { width: 'auto', text: texto, style: 'valor', noWrap: true, margin: [2, 0, 0, 0] },
  ],
  columnGap: 0,
});

const casillas = (opciones) => ({
  columns: opciones.map(([texto, activo]) => casilla(texto, activo)),
  columnGap: 16,
});

const encabezadoColumna = (texto, alignment = 'left', color = COLOR.azulOscuro, fillColor = COLOR.azulTinte) => ({
  text: texto,
  bold: true,
  fontSize: 6.5,
  color,
  fillColor,
  alignment,
});

const celda = (texto, alignment = 'left', extra = {}) => ({ text: mostrar(texto), style: 'celda', alignment, ...extra });

const celdaTotal = (texto, alignment = 'right', color = COLOR.azulOscuro) => ({
  text: mostrar(texto),
  bold: true,
  fontSize: 7.8,
  color,
  alignment,
});

// Una "pastilla" de color con texto blanco (folio, estado, urgente).
const pastilla = (texto, color, fontSize = 7) => ({
  table: {
    widths: ['auto'],
    body: [[{ text: texto, bold: true, fontSize, color: COLOR.blanco, fillColor: color, alignment: 'center', margin: [9, 2.5, 9, 2.5] }]],
  },
  layout: sinBordes,
});

const alDerecha = (nodo, margin = [0, 0, 0, 0]) => ({ columns: [{ width: '*', text: '' }, { width: 'auto', ...nodo }], margin });

// Encabezado: logo a la izquierda; titulo, folio y datos clave a la derecha.
const encabezado = ({ logo, nombreEmpresa, titulo: tituloDocumento, subtitulo, folio, insignias = [], detalles = [] }) => [
  {
    columns: [
      logo
        ? { width: 160, image: 'logoEmpresa', fit: [150, 60] }
        : { width: 160, text: nombreEmpresa, bold: true, fontSize: 12, color: COLOR.azulOscuro, margin: [0, 14, 0, 0] },
      {
        width: '*',
        stack: [
          { text: tituloDocumento, bold: true, fontSize: 18, color: COLOR.azulOscuro, alignment: 'right' },
          { text: subtitulo, fontSize: 8, color: COLOR.textoSuave, alignment: 'right', margin: [0, 1, 0, 0] },
          alDerecha(
            {
              columns: [
                ...insignias.map(([texto, color]) => ({ width: 'auto', ...pastilla(texto, color), margin: [0, 2, 0, 0] })),
                // El folio en azul oscuro: los colores de estado (durazno, verde, rojo) quedan
                // para las pastillas de estado y no se confunden con el.
                ...(folio ? [{ width: 'auto', ...pastilla(folio, COLOR.azulOscuro, 11) }] : []),
              ],
              columnGap: 6,
            },
            [0, 6, 0, 0]
          ),
          ...detalles.map((linea) => ({ text: linea, fontSize: 7.5, color: COLOR.textoSuave, alignment: 'right', margin: [0, 4, 0, 0] })),
        ],
      },
    ],
  },
  {
    canvas: [
      { type: 'rect', x: 0, y: 0, w: ANCHO_CONTENIDO, h: 2, color: COLOR.azul },
      { type: 'rect', x: 0, y: 0, w: 96, h: 2, color: COLOR.oro },
    ],
    margin: [0, 10, 0, 0],
  },
];

const pie = (izquierda) => (paginaActual, totalPaginas) => ({
  margin: [MARGEN, 12, MARGEN, 0],
  columns: [
    { text: izquierda, fontSize: 6.5, color: COLOR.textoSuave },
    { text: `${paginaActual} / ${totalPaginas}`, fontSize: 6.5, color: COLOR.textoSuave, alignment: 'right' },
  ],
});

// ---------------------------------------------------------------------------------------
// Datos
// ---------------------------------------------------------------------------------------

// Lista numerada de materiales solicitados a dos columnas, como el papel: 1-2-3 | 4-5-6.
// Se rellena a un minimo de 6 casillas y a numero par, asi el formato siempre se ve igual
// y el body de la tabla nunca queda vacio.
const numeroMaterial = (numero) => ({ text: String(numero), bold: true, fontSize: 7.5, color: COLOR.azul, alignment: 'right' });

const dividirMaterialesSolicitados = (listaMateriales = []) => {
  const descripciones = (listaMateriales || [])
    .map((material) => (material?.descripcion || '').trim())
    .filter(Boolean);
  let casillasTotales = Math.max(6, descripciones.length);
  if (casillasTotales % 2 === 1) casillasTotales += 1;
  const mitad = casillasTotales / 2;
  const filas = [];
  for (let i = 0; i < mitad; i++) {
    filas.push([
      numeroMaterial(i + 1),
      celda(descripciones[i]),
      numeroMaterial(mitad + i + 1),
      celda(descripciones[mitad + i]),
    ]);
  }
  return filas;
};

const crearResolverColorGrupo = () => {
  const asignados = new Map();
  return (grupo) => {
    const clave = (grupo || '').trim().toUpperCase();
    if (!clave) return null;
    if (!asignados.has(clave)) {
      asignados.set(clave, PALETA_GRUPOS[asignados.size % PALETA_GRUPOS.length]);
    }
    return asignados.get(clave);
  };
};

// Calibre y dimensiones son de cada pieza: van en su propia columna, no en la celda
// combinada del material.
const agruparFilasCotizacion = (filas = []) => {
  const colorPorGrupo = crearResolverColorGrupo();
  const body = [];
  let i = 0;
  while (i < filas.length) {
    const grupoActual = filas[i]?.grupo || '';
    const colorGrupo = filas[i]?.colorGrupo || colorPorGrupo(grupoActual);
    let span = 1;
    while (i + span < filas.length && (filas[i + span]?.grupo || '') === grupoActual) span++;
    for (let j = 0; j < span; j++) {
      const fila = filas[i + j];
      body.push([
        j === 0
          ? {
              rowSpan: span,
              text: grupoActual,
              fontSize: 7.5,
              bold: true,
              color: COLOR.texto,
              alignment: 'center',
              fillColor: colorGrupo || COLOR.azulTinte,
              margin: [0, span > 1 ? 8 : 1, 0, 0],
            }
          : {},
        celda(i + j + 1, 'center', { color: COLOR.textoSuave }),
        celda(fila?.calibre, 'center'),
        celda(fila?.dimensiones, 'center'),
        celda(fila?.nombreProyecto),
        celda(fila?.piezasTotales ?? '', 'right'),
        celda(fila?.materialRequerido),
        celda(fila?.metrosCorte ?? '', 'right'),
        celda(fila?.valorExtra1 ?? '', 'right'),
        celda(fila?.valorExtra2 ?? '', 'right'),
        celda(fila?.notas, 'left', { color: COLOR.textoSuave }),
      ]);
    }
    i += span;
  }
  return body;
};

// Redondeado a 2 decimales: 0.1 + 0.2 no debe imprimirse como 0.30000000000000004.
const redondear2 = (valor) => Math.round((Number(valor) || 0) * 100) / 100;
const sumar = (listaValores, campoSuma) =>
  redondear2((listaValores || []).reduce((acc, item) => acc + (Number(item?.[campoSuma]) || 0), 0));

// Desglose de piezas: misma regla que erp/src/Pages/Panel/PreCotizacionIngenieria/
// piezasPreCotizacion.js (este PDF es gemelo y no importa modulos de las pantallas).
const textoLimpio = (valor) => (valor === undefined || valor === null ? '' : String(valor).trim());

const etiquetaRenglon = (fila = {}, indice = 0) => {
  const material = [textoLimpio(fila.grupo), textoLimpio(fila.calibre), textoLimpio(fila.dimensiones)]
    .filter(Boolean)
    .join(' ');
  return [`#${indice + 1}`, material, textoLimpio(fila.nombreProyecto)].filter(Boolean).join(' · ');
};

const metrosTotalesPieza = (pieza = {}) => redondear2((Number(pieza.metrosPorPieza) || 0) * (Number(pieza.cantidad) || 0));

// En el orden de sus placas; las que no tienen placa (o ya no existe) al final.
const piezasOrdenadasPorPlaca = (piezas = [], filas = []) => {
  const indices = new Map();
  filas.forEach((fila, indice) => {
    const clave = textoLimpio(fila?.clave);
    if (clave && !indices.has(clave)) indices.set(clave, indice);
  });
  return piezas
    .map((pieza, posicion) => {
      const indice = indices.get(textoLimpio(pieza?.renglonClave));
      return {
        pieza: pieza || {},
        posicion,
        indice: indice === undefined ? Number.MAX_SAFE_INTEGER : indice,
        material: indice === undefined ? 'Sin placa' : etiquetaRenglon(filas[indice], indice),
      };
    })
    .sort((a, b) => a.indice - b.indice || a.posicion - b.posicion);
};

// Titulo y tabla del desglose; nada si no hay piezas (pdfmake truena con un body vacio).
const bloqueDesglosePiezas = (piezas = [], filas = []) => {
  if (piezas.length === 0) return [];
  const ordenadas = piezasOrdenadasPorPlaca(piezas, filas);
  const totalCantidad = sumar(piezas, 'cantidad');
  const totalMetros = redondear2(piezas.reduce((acc, pieza) => acc + metrosTotalesPieza(pieza || {}), 0));
  return [
    titulo('Desglose de piezas'),
    {
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['22%', '14%', '31%', '11%', '11%', '11%'],
        body: [
          [
            encabezadoColumna('NOMBRE DEL PROYECTO'),
            encabezadoColumna('NÚMERO DE PARTE'),
            encabezadoColumna('MATERIAL (PLACA)'),
            encabezadoColumna('METROS POR PIEZA', 'right'),
            encabezadoColumna('CANTIDAD', 'right'),
            encabezadoColumna('METROS TOTALES', 'right'),
          ],
          ...ordenadas.map(({ pieza, material }) => [
            celda(pieza.nombreProyecto),
            celda(pieza.numeroParte, 'left', { bold: true }),
            celda(material, 'left', material === 'Sin placa' ? { color: COLOR.textoSuave, italics: true } : { color: COLOR.textoSuave }),
            celda(pieza.metrosPorPieza ?? '', 'right'),
            celda(pieza.cantidad ?? '', 'right'),
            celda(metrosTotalesPieza(pieza), 'right'),
          ]),
          [
            { ...celdaTotal('Total', 'left'), colSpan: 4 }, {}, {}, {},
            celdaTotal(totalCantidad),
            celdaTotal(`${totalMetros} m`),
          ],
        ],
      },
      layout: tabla(),
    },
  ];
};

const bloqueExtras = (extras = []) => {
  if (extras.length === 0) return [];
  return [
    titulo('Extras (tornillería)', COLOR.verde),
    {
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['26%', '16%', '16%', '12%', '30%'],
        body: [
          [
            encabezadoColumna('TIPO', 'left', COLOR.verde, COLOR.verdeTinte),
            encabezadoColumna('MEDIDA', 'center', COLOR.verde, COLOR.verdeTinte),
            encabezadoColumna('LARGO', 'center', COLOR.verde, COLOR.verdeTinte),
            encabezadoColumna('PIEZAS', 'right', COLOR.verde, COLOR.verdeTinte),
            encabezadoColumna('NOTAS', 'left', COLOR.verde, COLOR.verdeTinte),
          ],
          ...extras.map((item) => [
            celda(item?.tipo),
            celda(item?.medida, 'center'),
            celda(item?.largo, 'center'),
            celda(item?.totalPiezas ?? '', 'right'),
            celda(item?.notas, 'left', { color: COLOR.textoSuave }),
          ]),
          [
            { ...celdaTotal('Total', 'left', COLOR.verde), colSpan: 3 }, {}, {},
            celdaTotal(sumar(extras, 'totalPiezas'), 'right', COLOR.verde),
            { text: '' },
          ],
        ],
      },
      layout: tabla(COLOR.verde),
    },
  ];
};

const nota = (etiqueta, texto) => ({
  stack: [
    { text: etiqueta.toUpperCase(), style: 'etiqueta' },
    { text: mostrar(texto), style: 'valor', margin: [0, 3, 0, 18] },
  ],
});

// ---------------------------------------------------------------------------------------
// Documento
// ---------------------------------------------------------------------------------------

const preCotizacionIngenieriaPdf = (values) => {
  const preCotizacion = values?.preCotizacion || {};
  const empresa = values?.empresa || {};

  const filasCotizacion = preCotizacion.filasCotizacion || [];
  const extras = preCotizacion.extras || [];

  const totalPiezas = sumar(filasCotizacion, 'piezasTotales');
  const totalMetros = sumar(filasCotizacion, 'metrosCorte');
  const totalExtra1 = sumar(filasCotizacion, 'valorExtra1');
  const totalExtra2 = sumar(filasCotizacion, 'valorExtra2');

  const tieneLogo = !!empresa?.logo?.base64Completa;
  const nombreEmpresa = empresa?.nombreComercial || empresa?.nombre || '';
  const folio = [preCotizacion.serie, preCotizacion.numero].filter((parte) => parte !== undefined && parte !== null && parte !== '').join(' ');

  const dd = {
    pageSize: 'LETTER',
    pageMargins: [MARGEN, MARGEN, MARGEN, 40],
    pageBreakBefore: evitarTituloHuerfano,
    content: [
      ...encabezado({
        logo: tieneLogo,
        nombreEmpresa,
        titulo: 'Pre cotización',
        subtitulo: 'Cotización previa por ingeniería',
        folio,
        insignias: preCotizacion.urgente === true ? [['URGENTE', COLOR.rojo]] : [],
        detalles: [preCotizacion.fechaSolicitud ? `Solicitud ${fecha(preCotizacion.fechaSolicitud)}` : null].filter(Boolean),
      }),

      titulo('Proyecto'),
      {
        table: {
          widths: ['40%', '30%', '30%'],
          body: [
            [
              campo('Nombre del proyecto', preCotizacion.nombreProyecto),
              campo('Orden de maquila', preCotizacion.ordenMaquila),
              campo('Encargado de proyecto', preCotizacion.encargadoProyecto),
            ],
            [
              campoNodo(
                'Urgente',
                casillas([
                  ['Sí', preCotizacion.urgente === true],
                  ['No', preCotizacion.urgente === false],
                ])
              ),
              campo('Fecha compromiso', fecha(preCotizacion.fechaCompromiso)),
              campo('Fecha de solicitud', fecha(preCotizacion.fechaSolicitud)),
            ],
          ],
        },
        layout: panel(),
      },

      titulo('Información del cliente'),
      {
        table: {
          widths: ['40%', '30%', '30%'],
          body: [
            [
              campo('Cliente', preCotizacion.cliente),
              campo('Contacto', preCotizacion.contacto),
              campoNodo(
                'Material',
                casillas([
                  ['Por cliente', !!preCotizacion.materialPorCliente],
                  ['Propio', !!preCotizacion.materialPropio],
                ])
              ),
            ],
            [
              campoNodo(
                'Programación de entrega del proyecto',
                casillas([
                  ['Recolección (cliente)', preCotizacion.recoleccionOEntrega === 'recoleccion'],
                  ['Entrega (chofer)', preCotizacion.recoleccionOEntrega === 'entrega'],
                ]),
                { colSpan: 3 }
              ),
              {},
              {},
            ],
          ],
        },
        layout: panel(),
      },

      // Igual que el formato de papel (el proveedor del material ya no va).
      titulo('Datos del material solicitado'),
      {
        table: {
          widths: ['50%', '50%'],
          body: [[
            campo('Procesos', preCotizacion.procesos),
            campo('Características del material', preCotizacion.caracteristicasGenerales),
          ]],
        },
        layout: panel(),
      },
      { text: 'MATERIALES SOLICITADOS', style: 'etiqueta', margin: [0, 8, 0, 2] },
      {
        table: {
          widths: [16, '*', 16, '*'],
          body: dividirMaterialesSolicitados(preCotizacion.caracteristicasMaterial),
        },
        layout: lista,
      },

      titulo('Cotización previa por ingeniería'),
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['9%', '3.5%', '7%', '10%', '14%', '7%', '12%', '8%', '8.5%', '8.5%', '12.5%'],
          body: [
            [
              encabezadoColumna('MATERIAL', 'center'),
              encabezadoColumna('#', 'center'),
              encabezadoColumna('CALIBRE', 'center'),
              encabezadoColumna('DIMENSIONES', 'center'),
              encabezadoColumna('NOMBRE DE PROYECTO'),
              encabezadoColumna('PIEZAS', 'right'),
              encabezadoColumna('MATERIAL REQUERIDO'),
              encabezadoColumna('METROS DE CORTE', 'right'),
              encabezadoColumna((preCotizacion.columnaExtra1Label || 'BAJADAS (CIZALLA)').toUpperCase(), 'right'),
              encabezadoColumna((preCotizacion.columnaExtra2Label || 'DOBLECES').toUpperCase(), 'right'),
              encabezadoColumna('NOTAS'),
            ],
            ...agruparFilasCotizacion(filasCotizacion),
            [
              { ...celdaTotal('Totales', 'left'), colSpan: 5 }, {}, {}, {}, {},
              celdaTotal(totalPiezas),
              { text: '' },
              celdaTotal(`${totalMetros} m`),
              celdaTotal(totalExtra1),
              celdaTotal(totalExtra2),
              { text: '' },
            ],
          ],
        },
        layout: tabla(),
      },
      alDerecha(
        {
          table: {
            body: [[
              { text: 'TIEMPO DE SOLDADURA', style: 'etiqueta', margin: [0, 1.5, 0, 0] },
              { text: mostrar(preCotizacion.tiempoSoldadura), bold: true, fontSize: 8.5, color: COLOR.texto },
            ]],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            fillColor: () => COLOR.amarilloTinte,
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 4,
            paddingBottom: () => 4,
          },
        },
        [0, 6, 0, 0]
      ),

      ...bloqueDesglosePiezas(preCotizacion.piezas || [], filasCotizacion),
      ...bloqueExtras(extras),

      // Dos notas finales: las que bajan a la orden de maquila y las que bajan a la
      // cotizacion.
      titulo('Notas'),
      {
        table: {
          widths: ['50%', '50%'],
          body: [[
            nota('Para la orden de maquila', preCotizacion.notas),
            nota('Para la cotización', preCotizacion.notasCotizacion),
          ]],
        },
        layout: panel(),
      },
    ],
    footer: pie([nombreEmpresa, folio ? `Pre cotización ${folio}` : 'Pre cotización'].filter(Boolean).join('  ·  ')),
    images: tieneLogo ? { logoEmpresa: empresa.logo.base64Completa } : {},
    defaultStyle: { fontSize: 7.5, color: COLOR.texto },
    styles: {
      etiqueta: { fontSize: 6.3, bold: true, color: COLOR.textoSuave },
      valor: { fontSize: 9, color: COLOR.texto },
      celda: { fontSize: 7.5, color: COLOR.texto },
    },
  };

  return dd;
};

export default preCotizacionIngenieriaPdf;
